<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_COT_FEE')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="camFeeRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="camLoanFeeList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: flat amount  -->
      <template #cell(flatAmount)="data">
        <p class="mb-0">
          {{ toBalance(data.value ) }}
        </p>
      </template>

      <!-- Column: flat amount per tranx -->
      <template #cell(flatAmountPerTransaction)="data">
        <p class="mb-0">
          {{ toBalance(data.value ) }}
        </p>
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_COT_FEE')"
              @click="onSelectCAMFeeItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectCAMFeeItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_COT_FEE' : 'ACTIVATE_COT_FEE')"
              @click="feeStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      no-close-on-backdrop
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="camLoanRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- Fee Name -->
            <b-col md="6">
              <InputField
                id="fee-name"
                v-model="camLoanFeeFormData.name"
                :label="$t('Fee Name')"
                :placeholder="$t('Enter fee name')"
                type="text"
                :error-name="$t('Fee Name')"
                rules="required"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Fee Name -->

            <!-- Flat Amount per Transaction -->
            <b-col md="6">
              <InputNumberField
                id="flat-amount-per-transaction"
                v-model.number="camLoanFeeFormData.flatAmountPerTransaction"
                :label="$t('Flat Amount per Transaction')"
                :placeholder="$t('Enter flat amount per transaction')"
                :error-name="$t('Flat Amount per Transaction')"
                rules="required|positive"
                :readonly="isReadonly"
                :is-allow-negative-numbers="false"
                onpaste="return false;"
              />
            </b-col>
            <!-- Flat Amount per Transaction -->
          </b-row>

          <b-row class="match-height">
            <!-- Percentage on Turnover (%) -->
            <b-col md="6">
              <InputField
                id="percentage-on-turnover"
                v-model.number="camLoanFeeFormData.percentageOnTurnover"
                :label="$t('Percentage on Turnover')"
                :placeholder="$t('Enter percentage on turnover')"
                type="number"
                :error-name="$t('Percentage on Turnover')"
                rules="required|max-value:100|positive"
                :readonly="isReadonly"
                :is-allow-negative-numbers="false"
                onpaste="return false;"
              />
            </b-col>
            <!-- Percentage on Turnover (%) -->

            <!-- Flat Amount -->
            <b-col md="6">
              <InputNumberField
                id="flat-amount"
                v-model.number="camLoanFeeFormData.flatAmount"
                :label="$t('Flat Amount')"
                :placeholder="$t('Enter flat amount')"
                :error-name="$t('Flat Amount')"
                rules="required|positive"
                :readonly="isReadonly"
                :is-allow-negative-numbers="false"
                onpaste="return false;"
              />
            </b-col>
          </b-row>
          <!-- Flat Amount -->
          <ValidationObserver>
            <b-row>
              <!--  Maximum Fee Amount -->
              <b-col md="6">
                <InputNumberField
                  id="maximum-fee"
                  v-model.number="camLoanFeeFormData.maximumAmount"
                  :label="$t('Maximum Fee Amount')"
                  :placeholder="$t('Enter maximum fee amount')"
                  type="number"
                  vid="maxFee"
                  :error-name="$t('maxFee')"
                  rules="required|positive"
                  :readonly="isReadonly"
                  :is-allow-negative-numbers="false"
                  onpaste="return false;"
                />
              </b-col>
              <!-- Maximum Fee Amount -->

              <!-- Minimum Fee Amount -->
              <b-col md="6">
                <InputNumberField
                  id="minimum-fee-amount"
                  v-model.number="camLoanFeeFormData.minimumAmount"
                  :label="$t('Minimum Fee Amount')"
                  :placeholder="$t('Enter minimum fee amount')"
                  type="number"
                  :error-name="$t('Minimum Fee Amount')"
                  rules="required|amount:@maxFee|positive"
                  :readonly="isReadonly"
                  :is-allow-negative-numbers="false"
                  onpaste="return false;"
                />
              </b-col>
              <!-- Minimum Fee Amount  -->
            </b-row>

          </ValidationObserver>
          <!-- Fee Charge Cycle -->
          <b-row class="match-height">
            <b-col md="6">
              <SelectField
                id="fee-charge-cycle"
                v-model="camLoanFeeFormData.chargeCycle"
                :label="$t('Fee Charge Cycle')"
                :placeholder="$t('Select')"
                :options="translatedChargeCycleOptions"
                :reduce="option => option.value"
                :error-name="$t('Fee Charge Cycle')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Fee Charge Cycle -->
            <!-- Fee Income GL -->
            <b-col md="6">
              <SelectField
                id="fee-income-gl"
                v-model="camLoanFeeFormData.incomeGLAccount"
                :label="$t('Fee Income GL')"
                :placeholder="$t('Select')"
                :options="incomeGlOptions"
                :reduce="option => option.value"
                :error-name="$t('Fee Income GL')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />

            </b-col>
            <!-- Fee Income GL -->
          </b-row>
          <b-row class="match-height">
            <!-- Transaction Type -->
            <b-col md="6">
              <SelectField
                id="transaction-type"
                v-model="camLoanFeeFormData.transactionType"
                :label="$t('Transaction Type')"
                :placeholder="$t('Select')"
                :options="translatedTransactionTypeOptions"
                :error-name="$t('Transaction Type')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Transaction Type -->

            <!-- Apply VAT  -->
            <b-col md="6">
              <b-form-group class="pt-2">
                <b-form-checkbox
                  v-model="camLoanFeeFormData.applyVAT"
                  checked="false"
                  name="apply-VAT-button"
                  switch
                  inline
                  :disabled="isReadonly"
                >
                  {{ $t('Apply VAT?') }}
                </b-form-checkbox>
              </b-form-group>

              <!-- Apply VAT  -->

            </b-col>

          </b-row>

        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="md"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchCAMFee"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="12">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
          />
        </b-col>
        <!-- Name -->

        <!-- Status -->
        <b-col md="12">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option=>option"
            :placeholder="$t('Select')"
            :options="statusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BFormGroup, BBadge,
  BForm, BFormCheckbox, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapGetters, mapActions } from 'vuex'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import {
  required, amount, positive,
} from '@validations'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toBalance, toKobo, formatAmount } from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../forms/InputField.vue'
import InputNumberField from '../../../forms/InputNumberField.vue'
import SelectField from '../../../forms/SelectField.vue'
import { toDateWithYear } from '../../../../helpers/formatDate'
import { computationModel, transactionType, camChargeCycle } from '@/helpers/data'
import getStatusColor from '../../../../helpers/getStatusColor'
import Button from '../../../components/button/Button.vue'
import { accountPermissions } from '../../../../helpers/permissions'
import convertZeroToString from '@/helpers/convertZeroToString'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BTable,
    BSpinner,
    BModal,
    BFormGroup,
    BForm,
    BFormCheckbox,
    ValidationObserver,
    BDropdown,
    BDropdownItem,
    InputField,
    InputNumberField,
    SelectField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      sortBy: 'flatAmount',
      showSpinner: false,
      required,
      amount,
      positive,
      toBalance,
      getStatusColor,
      modalTitle: this.$t('Create New CAM Fee'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'flatAmount', sortable: true, label: 'Flat Amount' },
        { key: 'flatAmountPerTransaction', sortable: true, label: 'Flat Amount per Tranx' },
        { key: 'percentageOnTurnover', sortable: true, label: '% on Turnover' },
        { key: 'transactionType', sortable: true, label: 'Transaction Type' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      camLoanFeeFormData: {
        name: '',
        flatAmountPerTransaction: null,
        percentageOnTurnover: null,
        flatAmount: null,
        chargeCycle: '',
        minimumAmount: null,
        maximumAmount: null,
        incomeGLAccount: '',
        transactionType: '',
        applyVAT: false,
      },
      // transactionTypeOptions: transactionType,
      feeComputationOptions: computationModel,
      // chargeCycleOptions: camChargeCycle,
      filter: {
        name: '',
        status: '',
        sortColumn: '',
      },
      camLoanFeeList: [],
    }
  },
  computed: {
    ...mapGetters({
      incomeGlOptions: 'account/incomeGlOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedChargeCycleOptions() {
      return camChargeCycle.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedTransactionTypeOptions() {
      return transactionType.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCAMFee()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCAMFee()
    },
  },
  mounted() {
    this.fetchCAMFee()
    this.incomeGLAccount({ permission: accountPermissions.VIEW_COT_FEE })
  },
  methods: {
    ...mapActions({
      incomeGLAccount: 'account/getIncomeGLAccount',
      getCAMFee: 'account/getCAMFeeList',
      updateCAMFee: 'account/updateCAMFeeAction',
      createCAMFee: 'account/createCAMFeeAction',
      updateCAMFeeStatus: 'account/updateStatus',
    }),
    async camFeeAction() {
      try {
        this.isLoading = true
        const flatAmount = toKobo(this.camLoanFeeFormData.flatAmount)
        const flatAmountPerTransaction = toKobo(this.camLoanFeeFormData.flatAmountPerTransaction)
        const minimumAmount = toKobo(this.camLoanFeeFormData.minimumAmount)
        const maximumAmount = toKobo(this.camLoanFeeFormData.maximumAmount)
        const formData = {
          ...this.camLoanFeeFormData,
          flatAmountPerTransaction,
          flatAmount,
          minimumAmount,
          maximumAmount,
        }

        const response = this.buttonLabel === this.$t('Save')
          ? await this.createCAMFee(formData)
          : await this.updateCAMFee(formData)

        const { status } = await response
        if (status === 200) {
          this.fetchCAMFee()
          this.hideModal('modal-lg')
          const requestText = this.$t('Your request to')
          const successText = this.$t('CAM Fee was successful')
          this.alertSuccess({ text: `${requestText} ${this.buttonLabel} ${successText}` })
        }
      } catch (error) {
        this.hideModal('modal-lg')
        error.response.data.errors.forEach(responseError => {
          const requestText = this.$t('Your request to')
          const failText = this.$t('CAM Fee failed. Reason:')
          const errorMessage = `${requestText} ${this.buttonLabel} ${failText} ${responseError.errorMessage}`
          this.alertError({ text: errorMessage })
        })
      }
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.camLoanRules.validate().then(async success => {
        if (success) {
          const areYouSureText = this.$t('Are you sure you want to')
          const camText = this.$t('this CAM Fee?')
          this.confirm(this.camFeeAction, { text: `${areYouSureText} ${this.buttonLabel} ${camText}` })
        }
      })
    },
    onSelectCAMFeeItem(items, activity, val) {
      try {
        this.camLoanFeeFormData.name = items.name
        this.camLoanFeeFormData.flatAmountPerTransaction = items.flatAmountPerTransaction === 0 ? convertZeroToString(items.flatAmountPerTransaction) : formatAmount(items.flatAmountPerTransaction)
        this.camLoanFeeFormData.percentageOnTurnover = items.percentageOnTurnover
        this.camLoanFeeFormData.flatAmount = items.flatAmount === 0 ? convertZeroToString(items.flatAmount) : formatAmount(items.flatAmount)
        this.camLoanFeeFormData.chargeCycle = items.chargeCycle
        this.camLoanFeeFormData.minimumAmount = items.minimumAmount === 0 ? convertZeroToString(items.minimumAmount) : formatAmount(items.minimumAmount)
        this.camLoanFeeFormData.maximumAmount = items.maximumAmount === 0 ? convertZeroToString(items.maximumAmount) : formatAmount(items.maximumAmount)
        this.camLoanFeeFormData.incomeGLAccount = items.incomeGLAccount
        this.camLoanFeeFormData.transactionType = items.transactionType
        this.camLoanFeeFormData.applyVAT = items.applyVAT
        this.camLoanFeeFormData.id = items.id
        this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
        this.modalTitle = val === 'edit' ? this.$t('Edit CAM Fee') : this.$t('CAM Fee')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async feeStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? 'Deactivate' : 'Activate'
      const permission = items.status === 'Active' ? accountPermissions.DEACTIVATE_COT_FEE : accountPermissions.ACTIVATE_COT_FEE

      const updateStatus = async () => {
        const requestText = this.$t('Your request to')
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateCAMFeeStatus({
            statusValues, actionType, routeName: 'COTFee', permission,
          })
          if (response.status === 200) {
            // const requestText = this.$t('Your request to')
            const successText = this.$t('CAM Fee was successful')
            this.fetchCAMFee()
            this.alertSuccess({ text: `${requestText} ${action} ${successText}` })
            this.showSpinner = false
          }
        } catch (error) {
          const failText = this.$t('CAM Fee failed. Reason:')
          this.alertError({ text: `${requestText} ${action} ${failText} ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSureText = this.$t('Are you sure you want to')
      const camText = this.$t('this CAM Fee?')
      this.confirm(updateStatus, { title: '', text: `${areYouSureText} ${action} ${camText}` })
    },
    async fetchCAMFee() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getCAMFee({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_COT_FEE,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.camLoanFeeList = data.data
          this.totalCount = data.recordCount
        } else {
          this.camLoanFeeList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    resetModal() {
      this.camLoanFeeFormData = { ...null }
      this.camLoanFeeFormData.applyVAT = false
      this.isReadonly = false
      this.isLoading = false
      this.modalTitle = this.$t('Create CAM Fee')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter.name = ''
      this.filter.status = ''
      this.filter.sortColumn = ''
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCAMFee()
    },

  },
}
</script>
